import { computed, inject, Injectable, Signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map, Observable, of, retry } from 'rxjs';
import { RegistrationType } from './types/registration-types';
import { COUNTRIES_REQUIRING_A2P_REGISTRATION, getCountriesRequiringRegulatoryCompliance } from '../constants';
import { OwnerService } from '../common/owner.service';
import { A2PRegistrationService } from './a2p-registration/a2p-registration.service';
import { RegulationService } from './regulatory-compliance/regulation.service';
import { RegistrationStage } from './a2p-registration/a2p-registration.constants';
import { RegulationStatus } from '@vendasta/protos/sms/v2/enums';
import { getPhoneAndRegistrationState$ } from '@vendasta/protos/sms/v2/sms-service';
import type { GetPhoneAndRegistrationStateResponse } from '@vendasta/protos/sms/v2/types';
import { SmsInjectionToken } from '../token';
import { toProtoOwnerType } from './owner-type';

export { toProtoOwnerType } from './owner-type';

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  private readonly ownerService = inject(OwnerService, { optional: true });
  private readonly a2pRegistrationService = inject(A2PRegistrationService);
  private readonly regulationService = inject(RegulationService);
  private readonly smsConfig = inject(SmsInjectionToken, { optional: true });

  private readonly ownerId = toSignal(this.smsConfig?.ownerId$ ?? of(undefined), { initialValue: undefined });

  /**
   * Resolved SMS setup state (registration regime + stage + phone provisioning) for the current owner,
   * from a single server-side call. Idle (value `undefined`) until the owner id is known and the call
   * resolves; retains the previous value across a {@link reloadPhoneAndRegistrationState} refetch.
   * Transient failures are retried before the resource surfaces an error.
   */
  private readonly stateResource = rxResource({
    params: () => {
      const config = this.smsConfig;
      const ownerId = this.ownerId();
      return config && ownerId ? { ownerId, ownerType: config.ownerType } : undefined;
    },
    stream: ({ params }) =>
      getPhoneAndRegistrationState$({ ownerId: params.ownerId, ownerType: toProtoOwnerType(params.ownerType) }).pipe(
        retry({ count: 2, delay: 2000 }),
      ),
  });

  // Guarded read: a resource's value() throws when read in the error state. hasValue() is false while
  // loading and on error, so this returns undefined in those cases instead of throwing. Pair with
  // stateIsLoading / stateErrored to tell "still loading" apart from "failed to load".
  readonly phoneAndRegistrationState: Signal<GetPhoneAndRegistrationStateResponse | undefined> = computed(() =>
    this.stateResource.hasValue() ? this.stateResource.value() : undefined,
  );
  readonly stateIsLoading: Signal<boolean> = this.stateResource.isLoading;
  readonly stateErrored: Signal<boolean> = computed(() => this.stateResource.status() === 'error');

  /** Re-fetch the resolved phone/registration state for the current owner. */
  reloadPhoneAndRegistrationState(): void {
    this.stateResource.reload();
  }

  /**
   * Determines the registration type required based on the owner's country.
   */
  getRegistrationType$(): Observable<RegistrationType> {
    if (!this.ownerService) {
      return of(RegistrationType.NONE);
    }

    return this.ownerService.getOwnerLocation$().pipe(
      map((ownerLocation) => {
        const country = ownerLocation.country?.toLowerCase();

        if (!country) {
          return RegistrationType.NONE;
        }

        // Check if country requires A2P registration
        if (COUNTRIES_REQUIRING_A2P_REGISTRATION.includes(country)) {
          return RegistrationType.A2P_REGISTRATION;
        }

        // Check if country requires regulatory compliance
        const countriesRequiringRegulatory = getCountriesRequiringRegulatoryCompliance();
        if (countriesRequiringRegulatory.includes(country)) {
          return RegistrationType.REGULATORY_COMPLIANCE;
        }

        return RegistrationType.NONE;
      }),
    );
  }

  isRegistrationRequired$(): Observable<boolean> {
    return this.getRegistrationType$().pipe(map((registrationType) => registrationType !== RegistrationType.NONE));
  }

  /**
   * Get the current registration stage based on registration type and status
   * This combines both A2P and regulation status to determine the overall stage
   */
  getRegistrationStage$(): Observable<RegistrationStage> {
    return combineLatest([
      this.getRegistrationType$(),
      this.a2pRegistrationService.registrationStage$,
      this.regulationService.registrationStage$,
    ]).pipe(
      map(([registrationType, a2pStage, regulationStage]) => {
        switch (registrationType) {
          case RegistrationType.A2P_REGISTRATION:
            return a2pStage;
          case RegistrationType.REGULATORY_COMPLIANCE:
            return regulationStage;
          case RegistrationType.NONE:
          default:
            return RegistrationStage.RegistrationComplete; // No registration needed
        }
      }),
    );
  }

  getRegulationStatus$(): Observable<RegulationStatus> {
    return this.regulationService.regulationStatus$;
  }
}
